import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import themeConfig from "./theme/themeConfig";

import DashboardLayout from "./components/DashboardLayout";
import PrivateRoute from "./components/PrivateRoute";
import PermissionRoute from "./components/PermissionRoute";

import Dashboard from "./pages/Dashboard";
import Infractions from "./pages/Infractions";
import InfractionsManagement from "./pages/InfractionsManagement";
import TypesInfractions from "./pages/TypesInfractions";
import PaymentPage from "./pages/PaymentPage";
import Users from "./pages/Users";
import Login from "./pages/Login";
import Unauthorized from "./pages/Unauthorized"; // You'll need to create this

import NotFound from "./components/NotFound";
import InfractionDetails from "./pages/InfractionDetails";
import LogViewer from "./pages/LogViewer";
import Maps from "./pages/Map";

import ZoneCreationPage from "./features/zones/ZoneCreationPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import CancellationReseans from "./components/CancellationReseans";
import StatisticsPage from "./pages/StatisticsPage";
import AgentStatistics from "./features/statistics/agents";
import InfractionsStatistics from "./features/statistics/infractions";
import PaymentsStatistics from "./features/statistics/payments";
import ZoneStatistics from "./features/statistics/zones";
import frFR from "antd/locale/fr_FR";
import SectionPage from "./features/sections/SectionPage";
import StreetPage from "./features/streets/StreetPage";
import PreviewMapPage from "./pages/PreviewMapPage";
import LinePage from "./features/lines/LinePage";
import TypeLines from "./pages/TypeLines";
import TransactionPage from "./pages/transaction/TransactionPage";
import Warnings from "./pages/Warnings";
import PrintInfractionsWarnings from "./pages/PrintInfractionsWarnings";
import WarningDetails from "./pages/WarningDetails";
import ConfigurationManager from "./features/configurationManager";
import VehiclePage from "./pages/VehiclePage";
import VehicleDetails from "./pages/VehicleDetails";
import PublicInstitutions from "./pages/PublicInstitutions";
import PermissionsPage from "./pages/permissions/PermissionsPage";
import AgentRolesPage from "./pages/agents/AgentRolesPage";
import RolePage from "./pages/roles/RolePage";
import TicketParking from "./pages/TicketParking";
import ClientsPage from "./pages/ClientsPage";
import ClientDetail from "./pages/ClientDetail";
import PDFFormFiller from "./PDFFormFiller";
import SubscriptionTypes from "./pages/subscriptionTypes/SubscriptionTypes";
import SubscriptionPage from "./pages/subscriptions";
import CreateSubscription from "./pages/subscriptions/CreateSubscription";
import ParkingCheckSimulation from "./pages/subscriptions/ParkingCheckSimulation";
import SubscriptionView from "./pages/subscriptions/SubscriptionView";
import VehicleUuidPage from "./pages/VehicleUuidPage";
import SettingsPage from "./pages/SettingsPage";
import VehicleWhitelistPage from "./pages/VehicleWhitelistPage";
import VehicleBlacklistPage from "./pages/VehicleBlacklistPage";
import WarningsStatistics from "./features/statistics/warnings";
import TicketParkingStatistics from "./features/statistics/ticket-Parking";
import SubscriptionsStatistics from "./features/statistics/subscriptions";
import PaymentsByZoneStatistics from "./features/statistics/payments-by-zone";
import VehicleStatistics from "./features/statistics/vehicles";
import UnpaidMonthlySubscriptions from "./features/statistics/unpaid-subscriptions";
import Devices from "./pages/Devices";
import SubscriptionAudiencesPage from "./pages/subscriptionAudiences/SubscriptionAudiencesPage";
import UserCriticalChangesPage from "./pages/UserCriticalChangesPage";
import DataCorrectionPage from "./pages/DataCorrectionPage";
import PaidMonthlySubscriptions from "./features/statistics/paid-subscriptions";

import DuplicatedVehiclesPage from "./pages/DuplicatedVehiclesPage";
import AccountDeletionRequestsPage from "./pages/AccountDeletionRequestsPage";
import CitizenUsers from "./pages/citizen/CitizenUsers";
import CitizenVehiclesPage from "./pages/citizen/CitizenVehiclesPage";
import CitizenTransactionsPage from "./pages/citizen/CitizenTransactionsPage";
import CitizenConsumptionPage from "./pages/citizen/CitizenConsumptionPage";
import CitizenParkingTicketsPage from "./pages/citizen/CitizenParkingTicketsPage";
import ParkingManagementPage from "./features/parkings/ParkingManagementPage";
import CitizenDisputesPage from "./pages/citizen/CitizenDisputesPage";
import CitizenSubscriptionRequests from "./pages/citizen/CitizenSubscriptionRequests";
import CitizenSubscriptionRequestDetails from "./pages/citizen/CitizenSubscriptionRequestDetails";
import CitizenRolesPage from "./pages/citizen/CitizenRolesPage";
import CitizenNotifiedInfractionsPage from "./pages/citizen/CitizenNotifiedInfractionsPage";
import AdminDashboard from "./pages/AdminDashboard";
import CitizenReclamationsPage from "./pages/citizen/CitizenReclamationsPage";
import CitizenWalletsPage from "./pages/citizen/CitizenWalletsPage";
import CitizenFatouratiPaymentsPage from "./pages/citizen/CitizenFatouratiPaymentsPage";
import CitizenCMIPaymentsPage from "./pages/citizen/CitizenCMIPaymentsPage";
import CitizenLogsPage from "./pages/citizen/CitizenLogsPage";

function App() {
  return (
    <ConfigProvider theme={themeConfig}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Protected routes using PrivateRoute */}
          <Route element={<PrivateRoute />}>
            <Route
              path="/"
              element={
                <ErrorBoundary>
                  <DashboardLayout />
                </ErrorBoundary>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path="dashboard"
                element={
                  <PermissionRoute
                    element={<Dashboard />}
                    requiredPermission={["dashboard", "menu"]}
                  />
                }
              />
              <Route
                path="warnings"
                element={
                  <PermissionRoute
                    element={<Warnings />}
                    requiredPermission={["warnings", "menu"]}
                  />
                }
              />
              <Route
                path="/warning/:id"
                element={
                  <PermissionRoute
                    element={<WarningDetails />}
                    requiredPermission={["warnings", "view"]}
                  />
                }
              />
              <Route
                path="infractions"
                element={
                  <PermissionRoute
                    element={<Infractions />}
                    requiredPermission={["infractions", "menu"]}
                  />
                }
              />
              <Route
                path="print-infractions-warnings"
                element={
                  <PermissionRoute
                    element={<PrintInfractionsWarnings />}
                    requiredPermission={[["infractions", "print_violation"], ["warnings", "print_warning"]]}
                  />
                }
              />
              <Route
                path="infractions-management"
                element={
                  <PermissionRoute
                    element={<InfractionsManagement />}
                    requiredPermission={["infractions", "menu"]}
                  />
                }
              />
              <Route
                path="/infraction/:id"
                element={
                  <PermissionRoute
                    element={<InfractionDetails />}
                    requiredPermission={["infractions", "view"]}
                  />
                }
              />
              <Route
                path="type-infraction"
                element={
                  <PermissionRoute
                    element={<TypesInfractions />}
                    requiredPermission={["type_infraction", "menu"]}
                  />
                }
              />
              <Route
                path="type-line"
                element={
                  <PermissionRoute
                    element={<TypeLines />}
                    requiredPermission={["type_line", "menu"]}
                  />
                }
              />
              <Route
                path="payment"
                element={
                  <PermissionRoute
                    element={<PaymentPage />}
                    requiredPermission={["payment", "menu"]}
                  />
                }
              />
              <Route
                path="transactions"
                element={
                  <PermissionRoute
                    element={<TransactionPage />}
                    requiredPermission={["transactions", "menu"]}
                  />
                }
              />
              <Route
                path="zone"
                element={
                  <PermissionRoute
                    element={<ZoneCreationPage />}
                    requiredPermission={["zone", "menu"]}
                  />
                }
              />
              <Route
                path="/zones/:zoneId/sections"
                element={
                  <PermissionRoute
                    element={<SectionPage />}
                    requiredPermission={["zone", "view"]}
                  />
                }
              />
              <Route
                path="/sections/:sectionId/streets"
                element={
                  <PermissionRoute
                    element={<StreetPage />}
                    requiredPermission={["sections", "view"]}
                  />
                }
              />
              <Route
                path="/streets/:streetId/lines"
                element={
                  <PermissionRoute
                    element={<LinePage />}
                    requiredPermission={["streets", "view"]}
                  />
                }
              />
              <Route
                path="preview-map"
                element={
                  <PermissionRoute
                    element={<PreviewMapPage />}
                    requiredPermission={["map", "view"]}
                  />
                }
              />
              <Route
                path="statistics"
                element={
                  <PermissionRoute
                    element={<StatisticsPage />}
                    requiredPermission={["statistics", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/users"
                element={
                  <PermissionRoute
                    element={<AgentStatistics />}
                    requiredPermission={["statistics_users", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/infractions"
                element={
                  <PermissionRoute
                    element={<InfractionsStatistics />}
                    requiredPermission={["statistics_infractions", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/payments"
                element={
                  <PermissionRoute
                    element={<PaymentsStatistics />}
                    requiredPermission={["statistics_payment", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/warnings"
                element={
                  <PermissionRoute
                    element={<WarningsStatistics />}
                    requiredPermission={["statistics_warnings", "menu"]}
                  />
                }
              />{" "}
              <Route
                path="statistics/parking-tickets"
                element={
                  <PermissionRoute
                    element={<TicketParkingStatistics />}
                    requiredPermission={["statistics_parking_tickets", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/subscriptions"
                element={
                  <PermissionRoute
                    element={<SubscriptionsStatistics />}
                    requiredPermission={["statistics_subscriptions", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/payments-by-zone"
                element={
                  <PermissionRoute
                    element={<PaymentsByZoneStatistics />}
                    requiredPermission={["statistics_payments", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/vehicles"
                element={
                  <PermissionRoute
                    element={<VehicleStatistics />}
                    requiredPermission={["statistics_vehicles", "menu"]}
                  />
                }
              />
              <Route
                path="statistics/unpaid-subscriptions"
                element={
                  <PermissionRoute
                    element={<UnpaidMonthlySubscriptions />}
                    requiredPermission={[
                      "statistics_unpaid_subscriptions",
                      "menu",
                    ]}
                  />
                }
              />
              <Route
                path="statistics/paid-subscriptions"
                element={
                  <PermissionRoute
                    element={<PaidMonthlySubscriptions />}
                    requiredPermission={[
                      "statistics_paid_subscriptions",
                      "menu",
                    ]}
                  />
                }
              />
              {/* <Route
                path="statistics/zones"
                element={
                  <PermissionRoute
                    element={<ZoneStatistics />}
                    requiredPermission={["lines", "menu"]}
                  />
                }
              /> */}
              <Route
                path="motifs-annulation"
                element={
                  <PermissionRoute
                    element={<CancellationReseans />}
                    requiredPermission={["motifs_annulation", "menu"]}
                  />
                }
              />
              <Route
                path="map"
                element={
                  <PermissionRoute
                    element={<Maps />}
                    requiredPermission={["map", "menu"]}
                  />
                }
              />
              <Route
                path="users"
                element={
                  <PermissionRoute
                    element={<Users />}
                    requiredPermission={["users", "menu"]}
                  />
                }
              />
              <Route
                path="ticket-parking"
                element={
                  <PermissionRoute
                    element={<TicketParking />}
                    requiredPermission={["ticket_parking", "menu"]}
                  />
                }
              />
              <Route
                path="vehicles"
                element={
                  <PermissionRoute
                    element={<VehiclePage />}
                    requiredPermission={["vehicles", "menu"]}
                  />
                }
              />
              <Route
                path="vehicle/:vehicleId"
                element={
                  <PermissionRoute
                    element={<VehicleDetails />}
                    requiredPermission={["vehicles", "view"]}
                  />
                }
              />
              <Route
                path="configuration-manager"
                element={
                  <PermissionRoute
                    element={<ConfigurationManager />}
                    requiredPermission={["configuration_manager", "menu"]}
                  />
                }
              />
              <Route
                path="public-institutions"
                element={
                  <PermissionRoute
                    element={<PublicInstitutions />}
                    requiredPermission={["public_institutions", "menu"]}
                  />
                }
              />
              <Route
                path="logs"
                element={
                  <PermissionRoute
                    element={<LogViewer />}
                    requiredPermission={["logs", "menu"]}
                  />
                }
              />
              <Route
                path="/roles"
                element={
                  <PermissionRoute
                    element={<RolePage />}
                    requiredPermission={["role", "view"]}
                  />
                }
              />
              <Route
                path="/permissions"
                element={
                  <PermissionRoute
                    element={<PermissionsPage />}
                    requiredPermission={["permission", "view"]}
                  />
                }
              />
              <Route
                path="/agent-roles"
                element={
                  <PermissionRoute
                    element={<AgentRolesPage />}
                    requiredPermission={["agent_role", "view"]}
                  />
                }
              />
              <Route
                path="/clients"
                element={
                  <PermissionRoute
                    element={<ClientsPage />}
                    requiredPermission={["clients", "menu"]}
                  />
                }
              />
              <Route
                path="/client/:id"
                element={
                  <PermissionRoute
                    element={<ClientDetail />}
                    requiredPermission={["clients", "view"]}
                  />
                }
              />
              <Route
                path="/type-subscription"
                element={
                  <PermissionRoute
                    element={<SubscriptionTypes />}
                    requiredPermission={["subscription_type", "menu"]}
                  />
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <PermissionRoute
                    element={<SubscriptionPage />}
                    requiredPermission={["subscriptions", "menu"]}
                  />
                }
              />
              <Route
                path="/subscriptions/create"
                element={
                  <PermissionRoute
                    element={<CreateSubscription />}
                    requiredPermission={["subscriptions", "create"]}
                  />
                }
              />
              <Route
                path="/subscriptions/:id"
                element={
                  <PermissionRoute
                    element={<SubscriptionView />}
                    requiredPermission={["subscriptions", "view"]}
                  />
                }
              />
              <Route
                path="/subscriptions/parking-check-simulation"
                element={
                  <PermissionRoute
                    element={<ParkingCheckSimulation />}
                    requiredPermission={["subscriptions", "menu"]}
                  />
                }
              />
              <Route
                path="/vehicle-uuids"
                element={
                  <PermissionRoute
                    element={<VehicleUuidPage />}
                    requiredPermission={["vehicle_uuids", "menu"]}
                  />
                }
              />
              <Route
                path="/settings"
                element={
                  <PermissionRoute
                    element={<SettingsPage />}
                    requiredPermission={["settings", "view"]}
                  />
                }
              />
              <Route
                path="/vehicle-whitelist"
                element={
                  <PermissionRoute
                    element={<VehicleWhitelistPage />}
                    requiredPermission={["whitelist_vehicles", "menu"]}
                  />
                }
              />
              <Route
                path="/vehicle-blacklist"
                element={
                  <PermissionRoute
                    element={<VehicleBlacklistPage />}
                    requiredPermission={["blacklist_vehicles", "menu"]}
                  />
                }
              />
              <Route
                path="/devices"
                element={
                  <PermissionRoute
                    element={<Devices />}
                    requiredPermission={["devices", "menu"]}
                  />
                }
              />
              <Route
                path="/subscription-audiences"
                element={
                  <PermissionRoute
                    element={<SubscriptionAudiencesPage />}
                    requiredPermission={["subscription_audience", "menu"]}
                  />
                }
              />
              <Route
                path="/user-critical-changes"
                element={
                  <PermissionRoute
                    element={<UserCriticalChangesPage />}
                    requiredPermission={["user_change", "menu"]}
                  />
                }
              />
              <Route
                path="/data-correction"
                element={
                  <PermissionRoute
                    element={<DataCorrectionPage />}
                    requiredPermission={["data_correction", "menu"]}
                  />
                }
              />
              <Route
                path="/duplicated-vehicles"
                element={
                  <PermissionRoute
                    element={<DuplicatedVehiclesPage />}
                    requiredPermission={["vehicles", "menu"]}
                  />
                }
              />
              {/* CITIZEN  */}
              <Route
                path="/citizen/users"
                element={
                  <PermissionRoute
                    element={<CitizenUsers />}
                    requiredPermission={["citizen_users", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/dashboard"
                element={
                  <PermissionRoute
                    element={<AdminDashboard />}
                    requiredPermission={["citizen_dashboard", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/transactions"
                element={
                  <PermissionRoute
                    element={<CitizenTransactionsPage />}
                    requiredPermission={["citizen_transaction", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/consumption"
                element={
                  <PermissionRoute
                    element={<CitizenConsumptionPage />}
                    requiredPermission={["citizen_transaction", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/vehicles"
                element={
                  <PermissionRoute  
                    element={<CitizenVehiclesPage />}
                    requiredPermission={["citizen_vehicles", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/parking-tickets"
                element={
                  <PermissionRoute
                    element={<CitizenParkingTicketsPage />}
                    requiredPermission={["citizen_parking_tickets", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/parkings"
                element={
                  <PermissionRoute
                    element={<ParkingManagementPage />}
                    requiredPermission={["citizen_parkings", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/disputes"
                element={
                  <PermissionRoute
                    element={<CitizenDisputesPage />}
                    requiredPermission={["citizen_dispute", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/complaints"
                element={
                  <PermissionRoute
                    element={<CitizenReclamationsPage />}
                    requiredPermission={["citizen_complaints", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/subscription-requests"
                element={
                  <PermissionRoute
                    element={<CitizenSubscriptionRequests />}
                    requiredPermission={[
                      "citizen_subscription_request",
                      "menu",
                    ]}
                  />
                }
              />
              <Route
                path="/citizen/subscription-requests/:id"
                element={
                  <PermissionRoute
                    element={<CitizenSubscriptionRequestDetails />}
                    requiredPermission={[
                      "citizen_subscription_request",
                      "menu",
                    ]}
                  />
                }
              />
              <Route
                path="/citizen/notified-infractions"
                element={
                  <PermissionRoute
                    element={<CitizenNotifiedInfractionsPage />}
                    requiredPermission={["citizen_notified_infractions", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/delete-requests"
                element={
                  <PermissionRoute
                    element={<AccountDeletionRequestsPage />}
                    requiredPermission={["citizen_delete_request", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/roles"
                element={
                  <PermissionRoute
                    element={<CitizenRolesPage />}
                    requiredPermission={["citizen_roles", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/wallets"
                element={
                  <PermissionRoute
                    element={<CitizenWalletsPage />}
                    requiredPermission={["citizen_wallets", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/fatourati-payments"
                element={
                  <PermissionRoute
                    element={<CitizenFatouratiPaymentsPage />}
                    requiredPermission={["citizen_fatourati_payments", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/cmi-payments"
                element={
                  <PermissionRoute
                    element={<CitizenCMIPaymentsPage />}
                    requiredPermission={["citizen_cmi_payments", "menu"]}
                  />
                }
              />
              <Route
                path="/citizen/logs"
                element={
                  <PermissionRoute
                    element={<CitizenLogsPage />}
                    requiredPermission={["citizen_logs", "menu"]}
                  />
                }
              />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
